import type { DagCompareLayoutRole, DagLayoutBounds, DagLayoutInput, DagLayoutLane, DagLayoutResult } from "./types";

const DEFAULT_RANK_GAP = 300;
const DEFAULT_NODE_GAP = 112;
const DEFAULT_COMPONENT_GAP = 180;
const DEFAULT_PADDING = 220;
const DEFAULT_ORDERING_PASSES = 4;

export interface FlowLayoutOptions {
  rankGap?: number;
  nodeGap?: number;
  componentGap?: number;
  padding?: number;
  orderingPasses?: number;
}

export interface CenteredLayoutOptions {
  rankGap?: number;
  nodeGap?: number;
  componentGap?: number;
  padding?: number;
  orderingPasses?: number;
  comparisonRoles?: Readonly<Record<string, DagCompareLayoutRole>>;
}

interface IndexedGraph {
  incoming: number[][];
  outgoing: number[][];
  undirected: number[][];
  stableNodes: number[];
  stableOrder: Int32Array;
  selfLoops: Uint8Array;
}

interface Components {
  componentByNode: Int32Array;
  members: number[][];
  keys: string[];
  cyclic: Uint8Array;
  incoming: number[][];
  outgoing: number[][];
  ranks: Int32Array;
}

interface WeakComponent {
  items: number[];
  key: string;
}

function compareText(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}

function finitePositive(value: number | undefined, fallback: number): number {
  return value != null && Number.isFinite(value) && value > 0 ? value : fallback;
}

function boundedPasses(value: number | undefined): number {
  if (value == null || !Number.isFinite(value)) return DEFAULT_ORDERING_PASSES;
  return Math.max(0, Math.min(12, Math.floor(value)));
}

function emptyLayout(nodeCount: number): DagLayoutResult {
  return {
    positions: new Float32Array(nodeCount * 2),
    bounds: { minX: 0, minY: 0, maxX: 800, maxY: 600 },
    lanes: [],
    ticks: [],
    temporal: false,
  };
}

function buildIndexedGraph(graph: DagLayoutInput): IndexedGraph {
  const nodeCount = graph.nodes.length;
  const incoming = Array.from({ length: nodeCount }, () => [] as number[]);
  const outgoing = Array.from({ length: nodeCount }, () => [] as number[]);
  const undirected = Array.from({ length: nodeCount }, () => [] as number[]);
  const selfLoops = new Uint8Array(nodeCount);
  const stableNodes = Array.from({ length: nodeCount }, (_, index) => index).sort((left, right) =>
    compareText(graph.nodes[left]!.id, graph.nodes[right]!.id) || left - right,
  );
  const stableOrder = new Int32Array(nodeCount);
  for (let order = 0; order < stableNodes.length; order += 1) stableOrder[stableNodes[order]!] = order;

  for (const edge of graph.edges) {
    const { source, target } = edge;
    if (source < 0 || target < 0 || source >= nodeCount || target >= nodeCount) continue;
    outgoing[source]!.push(target);
    incoming[target]!.push(source);
    if (source === target) {
      selfLoops[source] = 1;
    } else {
      undirected[source]!.push(target);
      undirected[target]!.push(source);
    }
  }

  const compareNode = (left: number, right: number) => stableOrder[left]! - stableOrder[right]!;
  for (let index = 0; index < nodeCount; index += 1) {
    incoming[index]!.sort(compareNode);
    outgoing[index]!.sort(compareNode);
    undirected[index]!.sort(compareNode);
  }
  return { incoming, outgoing, undirected, stableNodes, stableOrder, selfLoops };
}

/** Iterative Kosaraju traversal: safe for very deep chains and large cyclic graphs. */
function stronglyConnectedComponents(graph: DagLayoutInput, indexed: IndexedGraph): Components {
  const nodeCount = graph.nodes.length;
  const visited = new Uint8Array(nodeCount);
  const finishOrder: number[] = [];

  for (const start of indexed.stableNodes) {
    if (visited[start]) continue;
    visited[start] = 1;
    const nodeStack = [start];
    const edgeStack = [0];
    while (nodeStack.length > 0) {
      const frame = nodeStack.length - 1;
      const node = nodeStack[frame]!;
      const edgeOffset = edgeStack[frame]!;
      const targets = indexed.outgoing[node]!;
      if (edgeOffset < targets.length) {
        const target = targets[edgeOffset]!;
        edgeStack[frame] = edgeOffset + 1;
        if (!visited[target]) {
          visited[target] = 1;
          nodeStack.push(target);
          edgeStack.push(0);
        }
      } else {
        finishOrder.push(node);
        nodeStack.pop();
        edgeStack.pop();
      }
    }
  }

  const componentByNode = new Int32Array(nodeCount);
  componentByNode.fill(-1);
  const members: number[][] = [];
  for (let order = finishOrder.length - 1; order >= 0; order -= 1) {
    const start = finishOrder[order]!;
    if (componentByNode[start] !== -1) continue;
    const component = members.length;
    const componentMembers: number[] = [];
    const stack = [start];
    componentByNode[start] = component;
    while (stack.length > 0) {
      const node = stack.pop()!;
      componentMembers.push(node);
      const sources = indexed.incoming[node]!;
      for (let index = sources.length - 1; index >= 0; index -= 1) {
        const source = sources[index]!;
        if (componentByNode[source] === -1) {
          componentByNode[source] = component;
          stack.push(source);
        }
      }
    }
    componentMembers.sort((left, right) => indexed.stableOrder[left]! - indexed.stableOrder[right]!);
    members.push(componentMembers);
  }

  const count = members.length;
  const keys = members.map((nodes) => graph.nodes[nodes[0]!]!.id);
  const cyclic = new Uint8Array(count);
  for (let component = 0; component < count; component += 1) {
    const nodes = members[component]!;
    if (nodes.length > 1 || indexed.selfLoops[nodes[0]!] === 1) cyclic[component] = 1;
  }

  const outgoing = Array.from({ length: count }, () => [] as number[]);
  const incoming = Array.from({ length: count }, () => [] as number[]);
  for (const edge of graph.edges) {
    if (edge.source < 0 || edge.target < 0 || edge.source >= nodeCount || edge.target >= nodeCount) continue;
    const source = componentByNode[edge.source]!;
    const target = componentByNode[edge.target]!;
    if (source !== target) outgoing[source]!.push(target);
  }
  const compareComponent = (left: number, right: number) => compareText(keys[left]!, keys[right]!) || left - right;
  for (let source = 0; source < count; source += 1) {
    const targets = outgoing[source]!;
    targets.sort(compareComponent);
    let write = 0;
    for (let read = 0; read < targets.length; read += 1) {
      if (read === 0 || targets[read] !== targets[read - 1]) targets[write++] = targets[read]!;
    }
    targets.length = write;
    for (const target of targets) incoming[target]!.push(source);
  }
  for (const sources of incoming) sources.sort(compareComponent);

  const ranks = componentRanks(outgoing, incoming, compareComponent);
  return { componentByNode, members, keys, cyclic, incoming, outgoing, ranks };
}

class StableMinHeap {
  private readonly items: number[] = [];

  constructor(private readonly compare: (left: number, right: number) => number) {}

  get size(): number {
    return this.items.length;
  }

  push(value: number): void {
    const items = this.items;
    let index = items.length;
    items.push(value);
    while (index > 0) {
      const parent = Math.floor((index - 1) / 2);
      if (this.compare(items[parent]!, value) <= 0) break;
      items[index] = items[parent]!;
      index = parent;
    }
    items[index] = value;
  }

  pop(): number {
    const items = this.items;
    const first = items[0]!;
    const last = items.pop()!;
    if (items.length === 0) return first;
    let index = 0;
    while (true) {
      const left = index * 2 + 1;
      if (left >= items.length) break;
      const right = left + 1;
      const child = right < items.length && this.compare(items[right]!, items[left]!) < 0 ? right : left;
      if (this.compare(last, items[child]!) <= 0) break;
      items[index] = items[child]!;
      index = child;
    }
    items[index] = last;
    return first;
  }
}

function componentRanks(
  outgoing: number[][],
  incoming: number[][],
  compareComponent: (left: number, right: number) => number,
): Int32Array {
  const count = outgoing.length;
  const indegree = new Int32Array(count);
  const ranks = new Int32Array(count);
  const ready = new StableMinHeap(compareComponent);
  for (let component = 0; component < count; component += 1) {
    indegree[component] = incoming[component]!.length;
    if (indegree[component] === 0) ready.push(component);
  }
  while (ready.size > 0) {
    const source = ready.pop();
    for (const target of outgoing[source]!) {
      ranks[target] = Math.max(ranks[target]!, ranks[source]! + 1);
      indegree[target] = indegree[target]! - 1;
      if (indegree[target] === 0) ready.push(target);
    }
  }
  return ranks;
}

function weakComponents(
  items: number[],
  incoming: number[][],
  outgoing: number[][],
  keyFor: (item: number) => string,
): WeakComponent[] {
  const included = new Uint8Array(incoming.length);
  const visited = new Uint8Array(incoming.length);
  for (const item of items) included[item] = 1;
  const stableItems = items.slice().sort((left, right) => compareText(keyFor(left), keyFor(right)) || left - right);
  const result: WeakComponent[] = [];
  for (const start of stableItems) {
    if (visited[start]) continue;
    visited[start] = 1;
    const componentItems: number[] = [];
    const stack = [start];
    while (stack.length > 0) {
      const item = stack.pop()!;
      componentItems.push(item);
      for (const neighbor of outgoing[item]!) {
        if (included[neighbor] && !visited[neighbor]) {
          visited[neighbor] = 1;
          stack.push(neighbor);
        }
      }
      for (const neighbor of incoming[item]!) {
        if (included[neighbor] && !visited[neighbor]) {
          visited[neighbor] = 1;
          stack.push(neighbor);
        }
      }
    }
    componentItems.sort((left, right) => compareText(keyFor(left), keyFor(right)) || left - right);
    result.push({ items: componentItems, key: keyFor(componentItems[0]!) });
  }
  result.sort((left, right) => compareText(left.key, right.key));
  return result;
}

function orderLayers(
  layers: Map<number, number[]>,
  incoming: number[][],
  outgoing: number[][],
  stableOrder: Int32Array,
  passes: number,
  weight: (item: number) => number,
): void {
  const layerKeys = [...layers.keys()].sort((left, right) => left - right);
  const positions = new Float64Array(stableOrder.length);
  positions.fill(Number.NaN);

  const updatePositions = (items: number[]) => {
    let cursor = 0;
    for (const item of items) {
      const itemWeight = Math.max(1, weight(item));
      positions[item] = cursor + itemWeight / 2;
      cursor += itemWeight;
    }
  };
  for (const key of layerKeys) updatePositions(layers.get(key)!);

  const reorder = (items: number[], neighbors: number[][]) => {
    const previous = new Float64Array(items.length);
    const scores = new Float64Array(items.length);
    const hasScore = new Uint8Array(items.length);
    for (let itemIndex = 0; itemIndex < items.length; itemIndex += 1) {
      const item = items[itemIndex]!;
      previous[itemIndex] = positions[item]!;
      let total = 0;
      let count = 0;
      for (const neighbor of neighbors[item]!) {
        const position = positions[neighbor]!;
        if (Number.isFinite(position)) {
          total += position;
          count += 1;
        }
      }
      if (count > 0) {
        hasScore[itemIndex] = 1;
        scores[itemIndex] = total / count;
      }
    }
    const metrics = new Map<number, { score: number; scored: boolean; previous: number }>();
    for (let index = 0; index < items.length; index += 1) {
      metrics.set(items[index]!, { score: scores[index]!, scored: hasScore[index] === 1, previous: previous[index]! });
    }
    items.sort((left, right) => {
      const leftMetric = metrics.get(left)!;
      const rightMetric = metrics.get(right)!;
      if (leftMetric.scored !== rightMetric.scored) return leftMetric.scored ? -1 : 1;
      if (leftMetric.scored && leftMetric.score !== rightMetric.score) return leftMetric.score - rightMetric.score;
      if (leftMetric.previous !== rightMetric.previous) return leftMetric.previous - rightMetric.previous;
      return stableOrder[left]! - stableOrder[right]!;
    });
    updatePositions(items);
  };

  for (let pass = 0; pass < passes; pass += 1) {
    for (const key of layerKeys) reorder(layers.get(key)!, incoming);
    for (let index = layerKeys.length - 1; index >= 0; index -= 1) reorder(layers.get(layerKeys[index]!)!, outgoing);
  }
}

function finalBounds(maxX: number, maxY: number, padding: number): DagLayoutBounds {
  return {
    minX: 0,
    minY: 0,
    maxX: Math.max(1_000, maxX + padding),
    maxY: Math.max(800, maxY + padding),
  };
}

/**
 * Deterministic, left-to-right layered layout. Strongly connected components
 * occupy one rank, so cycles are represented honestly instead of being broken
 * by arbitrary edge deletion.
 */
export function computeFlowLayout(graph: DagLayoutInput, options: FlowLayoutOptions = {}): DagLayoutResult {
  const nodeCount = graph.nodes.length;
  if (nodeCount === 0) return emptyLayout(0);
  const rankGap = finitePositive(options.rankGap, DEFAULT_RANK_GAP);
  const nodeGap = finitePositive(options.nodeGap, DEFAULT_NODE_GAP);
  const componentGap = finitePositive(options.componentGap, DEFAULT_COMPONENT_GAP);
  const padding = finitePositive(options.padding, DEFAULT_PADDING);
  const indexed = buildIndexedGraph(graph);
  const components = stronglyConnectedComponents(graph, indexed);
  const componentCount = components.members.length;
  const componentOrder = new Int32Array(componentCount);
  const stableComponents = Array.from({ length: componentCount }, (_, index) => index).sort((left, right) =>
    compareText(components.keys[left]!, components.keys[right]!) || left - right,
  );
  for (let order = 0; order < stableComponents.length; order += 1) componentOrder[stableComponents[order]!] = order;
  const connected = weakComponents(
    stableComponents,
    components.incoming,
    components.outgoing,
    (component) => components.keys[component]!,
  );
  const positions = new Float32Array(nodeCount * 2);
  const lanes: DagLayoutLane[] = [];
  let bandTop = padding;
  let maxX = padding;

  for (let bandIndex = 0; bandIndex < connected.length; bandIndex += 1) {
    const connectedComponent = connected[bandIndex]!;
    const layers = new Map<number, number[]>();
    let maxRank = 0;
    let cycleCount = 0;
    for (const component of connectedComponent.items) {
      const rank = components.ranks[component]!;
      maxRank = Math.max(maxRank, rank);
      if (components.cyclic[component]) cycleCount += 1;
      const layer = layers.get(rank);
      if (layer) layer.push(component);
      else layers.set(rank, [component]);
    }
    for (const layer of layers.values()) layer.sort((left, right) => componentOrder[left]! - componentOrder[right]!);
    orderLayers(
      layers,
      components.incoming,
      components.outgoing,
      componentOrder,
      boundedPasses(options.orderingPasses),
      (component) => components.members[component]!.length,
    );

    let bandSlots = 1;
    for (const layer of layers.values()) {
      let slots = 0;
      for (const component of layer) slots += components.members[component]!.length;
      bandSlots = Math.max(bandSlots, slots);
    }
    const bandHeight = Math.max(nodeGap * 2, bandSlots * nodeGap);
    const bandCenter = bandTop + bandHeight / 2;
    const plural = cycleCount === 1 ? "cycle" : "cycles";
    const baseLabel = connected.length === 1 ? "Flow" : `Component ${bandIndex + 1}`;
    lanes.push({
      key: `flow:${connectedComponent.key}`,
      label: cycleCount > 0 ? `${baseLabel} · ${cycleCount} ${plural}` : baseLabel,
      y: bandCenter,
    });

    for (const [rank, layer] of layers) {
      let totalSlots = 0;
      for (const component of layer) totalSlots += components.members[component]!.length;
      let y = bandCenter - (totalSlots * nodeGap) / 2 + nodeGap / 2;
      for (const component of layer) {
        for (const node of components.members[component]!) {
          positions[node * 2] = padding + rank * rankGap;
          positions[node * 2 + 1] = y;
          y += nodeGap;
        }
      }
      maxX = Math.max(maxX, padding + rank * rankGap);
    }
    bandTop += bandHeight + componentGap;
  }

  return {
    positions,
    bounds: finalBounds(maxX, Math.max(padding, bandTop - componentGap), padding),
    lanes,
    ticks: [],
    temporal: false,
  };
}

function distances(starts: number[], adjacency: number[][]): Int32Array {
  const result = new Int32Array(adjacency.length);
  result.fill(-1);
  const queue = starts.slice();
  for (const start of starts) result[start] = 0;
  for (let head = 0; head < queue.length; head += 1) {
    const source = queue[head]!;
    for (const target of adjacency[source]!) {
      if (result[target] !== -1) continue;
      result[target] = result[source]! + 1;
      queue.push(target);
    }
  }
  return result;
}

function chooseDefaultAnchor(indexed: IndexedGraph): number {
  let selected = indexed.stableNodes[0]!;
  let selectedDegree = -1;
  for (const node of indexed.stableNodes) {
    const degree = indexed.incoming[node]!.length + indexed.outgoing[node]!.length;
    if (degree > selectedDegree) {
      selected = node;
      selectedDegree = degree;
    }
  }
  return selected;
}

/**
 * Places one or more anchors at the centre, their ancestry to the left and
 * descendants to the right. Side branches that are neither are kept in stable
 * context bands below the focused lineage.
 */
export function computeCenteredLayout(
  graph: DagLayoutInput,
  anchorIds: string[],
  options: CenteredLayoutOptions = {},
): DagLayoutResult {
  const nodeCount = graph.nodes.length;
  if (nodeCount === 0) return emptyLayout(0);
  const rankGap = finitePositive(options.rankGap, DEFAULT_RANK_GAP);
  const nodeGap = finitePositive(options.nodeGap, DEFAULT_NODE_GAP);
  const componentGap = finitePositive(options.componentGap, DEFAULT_COMPONENT_GAP);
  const padding = finitePositive(options.padding, DEFAULT_PADDING);
  const indexed = buildIndexedGraph(graph);
  const indexById = new Map(graph.nodes.map((node, index) => [node.id, index]));
  const anchorSet = new Set<number>();
  for (const id of anchorIds) {
    const index = indexById.get(id);
    if (index != null) anchorSet.add(index);
  }
  if (anchorSet.size === 0) anchorSet.add(chooseDefaultAnchor(indexed));
  const anchors = [...anchorSet].sort((left, right) => indexed.stableOrder[left]! - indexed.stableOrder[right]!);
  const upstream = distances(anchors, indexed.incoming);
  const downstream = distances(anchors, indexed.outgoing);
  const levels = new Int32Array(nodeCount);
  const contextual: number[] = [];
  const isContextual = new Uint8Array(nodeCount);
  let sideDepth = 1;

  for (let node = 0; node < nodeCount; node += 1) {
    if (anchorSet.has(node)) {
      levels[node] = 0;
      continue;
    }
    const up = upstream[node]!;
    const down = downstream[node]!;
    if (up === -1 && down === -1) {
      levels[node] = 0;
      contextual.push(node);
      isContextual[node] = 1;
      continue;
    }
    let level: number;
    if (down === -1 || (up !== -1 && up < down)) level = -up;
    else if (up === -1 || down < up) level = down;
    else level = indexed.stableOrder[node]! % 2 === 0 ? -up : down;
    levels[node] = level;
    sideDepth = Math.max(sideDepth, Math.abs(level));
  }

  const focusedLayers = new Map<number, number[]>();
  for (let node = 0; node < nodeCount; node += 1) {
    if (isContextual[node]) continue;
    const level = levels[node]!;
    const layer = focusedLayers.get(level);
    if (layer) layer.push(node);
    else focusedLayers.set(level, [node]);
  }
  for (const layer of focusedLayers.values()) {
    layer.sort((left, right) => indexed.stableOrder[left]! - indexed.stableOrder[right]!);
  }
  orderLayers(
    focusedLayers,
    indexed.incoming,
    indexed.outgoing,
    indexed.stableOrder,
    boundedPasses(options.orderingPasses),
    () => 1,
  );

  const comparisonRoles = options.comparisonRoles;
  const comparisonBand = (node: number): "a" | "shared" | "b" => {
    const role = comparisonRoles?.[graph.nodes[node]!.id];
    if (role === "a" || role === "anchor-a") return "a";
    if (role === "b" || role === "anchor-b") return "b";
    return "shared";
  };
  const hasComparisonBands = comparisonRoles != null && anchors.some((node) => {
    const role = comparisonRoles[graph.nodes[node]!.id];
    return role === "anchor-a" || role === "anchor-b";
  });
  const bandOrder = ["a", "shared", "b"] as const;
  const bandLabels = { a: "A lineage", shared: "Shared ancestry", b: "B lineage" } as const;
  const bandSlots = { a: 0, shared: 0, b: 0 };
  if (hasComparisonBands) {
    for (const layer of focusedLayers.values()) {
      const counts = { a: 0, shared: 0, b: 0 };
      for (const node of layer) counts[comparisonBand(node)] += 1;
      for (const band of bandOrder) bandSlots[band] = Math.max(bandSlots[band], counts[band]);
    }
  }
  const presentBands = hasComparisonBands ? bandOrder.filter((band) => bandSlots[band] > 0) : [];
  const bandGap = nodeGap * 0.7;
  const bandCenters = new Map<"a" | "shared" | "b", number>();
  let focusHeight: number;
  if (presentBands.length > 0) {
    let cursorY = padding;
    for (const band of presentBands) {
      const height = Math.max(nodeGap, bandSlots[band] * nodeGap);
      bandCenters.set(band, cursorY + height / 2);
      cursorY += height + bandGap;
    }
    focusHeight = cursorY - padding - bandGap;
  } else {
    let focusSlots = 1;
    for (const layer of focusedLayers.values()) focusSlots = Math.max(focusSlots, layer.length);
    focusHeight = Math.max(nodeGap * 2, focusSlots * nodeGap);
  }
  const focusCenterY = padding + focusHeight / 2;
  const centerX = padding + sideDepth * rankGap;
  const positions = new Float32Array(nodeCount * 2);
  positions.fill(Number.NaN);
  for (const [level, layer] of focusedLayers) {
    if (presentBands.length > 0) {
      const nodesByBand = { a: [] as number[], shared: [] as number[], b: [] as number[] };
      for (const node of layer) nodesByBand[comparisonBand(node)].push(node);
      for (const band of presentBands) {
        const bandNodes = nodesByBand[band];
        let y = bandCenters.get(band)! - (bandNodes.length * nodeGap) / 2 + nodeGap / 2;
        for (const node of bandNodes) {
          positions[node * 2] = centerX + level * rankGap;
          positions[node * 2 + 1] = y;
          y += nodeGap;
        }
      }
    } else {
      let y = focusCenterY - (layer.length * nodeGap) / 2 + nodeGap / 2;
      for (const node of layer) {
        positions[node * 2] = centerX + level * rankGap;
        positions[node * 2 + 1] = y;
        y += nodeGap;
      }
    }
  }

  const lanes: DagLayoutLane[] = presentBands.length > 0
    ? presentBands.map((band) => ({ key: `centered:compare:${band}`, label: bandLabels[band], y: bandCenters.get(band)! }))
    : [{ key: "centered:focus", label: "Focused lineage", y: focusCenterY }];
  let maxY = padding + focusHeight;
  let minContextX = centerX;
  let maxContextX = centerX;
  if (contextual.length > 0) {
    const contextIncluded = new Uint8Array(nodeCount);
    for (const node of contextual) contextIncluded[node] = 1;
    const contextIncoming = indexed.incoming.map((sources) => sources.filter((source) => contextIncluded[source]));
    const contextOutgoing = indexed.outgoing.map((targets) => targets.filter((target) => contextIncluded[target]));
    const groups = weakComponents(
      contextual,
      contextIncoming,
      contextOutgoing,
      (node) => graph.nodes[node]!.id,
    );
    let contextTop = maxY + componentGap;
    for (let groupIndex = 0; groupIndex < groups.length; groupIndex += 1) {
      const group = groups[groupIndex]!;
      const columns = Math.max(1, Math.min(sideDepth * 2 + 1, Math.ceil(Math.sqrt(group.items.length))));
      const rows = Math.ceil(group.items.length / columns);
      const groupHeight = Math.max(nodeGap, rows * nodeGap);
      const groupCenterY = contextTop + groupHeight / 2;
      lanes.push({
        key: `centered:context:${group.key}`,
        label: groups.length === 1 ? "Context" : `Context ${groupIndex + 1}`,
        y: groupCenterY,
      });
      for (let index = 0; index < group.items.length; index += 1) {
        const node = group.items[index]!;
        const column = index % columns;
        const row = Math.floor(index / columns);
        const x = centerX + (column - (columns - 1) / 2) * rankGap;
        positions[node * 2] = x;
        positions[node * 2 + 1] = contextTop + row * nodeGap + nodeGap / 2;
        minContextX = Math.min(minContextX, x);
        maxContextX = Math.max(maxContextX, x);
      }
      contextTop += groupHeight + componentGap;
    }
    maxY = contextTop - componentGap;
  }

  const minX = Math.min(padding, centerX - sideDepth * rankGap, minContextX);
  const shiftX = minX < padding ? padding - minX : 0;
  if (shiftX > 0) {
    for (let node = 0; node < nodeCount; node += 1) positions[node * 2] = positions[node * 2]! + shiftX;
  }
  const focusedMaxX = centerX + sideDepth * rankGap;
  return {
    positions,
    bounds: finalBounds(Math.max(focusedMaxX, maxContextX) + shiftX, maxY, padding),
    lanes,
    ticks: [],
    temporal: false,
  };
}
